import type { Attachment } from "./attachments";
import { apiRequest } from "./client";
import type { ProjectCloudDriveInfoResponse, ProjectDetail, ProjectStatus } from "./projects";

export interface ReviewAssignment {
  id: string;
  project_id: string;
  project_number: string;
  work_name: string;
  owner_name: string;
  reviewer_user_id: string;
  reviewer_username: string;
  reviewer_name: string;
  assigned_by_name: string;
  assigned_at: string;
  active: boolean;
  revoked_at: string | null;
}

export interface ReviewAssignmentListResponse {
  items: ReviewAssignment[];
  total: number;
  page: number;
  page_size: number;
}

export interface ReviewAssignmentCreateResponse {
  created: ReviewAssignment[];
  already_active_count: number;
}

export interface ReviewerOption {
  id: string;
  username: string;
  name: string;
  active_task_count: number;
  is_idle: boolean;
}

export interface ReviewTaskListItem {
  id: string;
  project_id: string;
  project_number: string;
  work_name: string;
  work_type_code: string;
  owner_name: string;
  reviewer_user_id: string;
  reviewer_username: string;
  reviewer_name: string;
  project_status: ProjectStatus;
  assigned_at: string;
  active_attachment_count: number;
  updated_at: string;
}

export interface ReviewTaskListResponse {
  items: ReviewTaskListItem[];
  total: number;
  page: number;
  page_size: number;
}

export interface ReviewTaskDetail {
  assignment_id: string;
  assigned_at: string;
  project: ProjectDetail;
  attachments: Attachment[];
}

export function fetchReviewerOptions(search?: string): Promise<ReviewerOption[]> {
  const params = new URLSearchParams();
  if (search) params.set("search", search);
  const query = params.size ? `?${params}` : "";
  return apiRequest(`/api/v1/admin/review-assignments/reviewer-options${query}`);
}

export function fetchReviewAssignments(
  filters: { page: number; pageSize: number; search?: string; active: boolean },
  signal?: AbortSignal,
): Promise<ReviewAssignmentListResponse> {
  const params = new URLSearchParams({
    page: String(filters.page),
    page_size: String(filters.pageSize),
    active: String(filters.active),
  });
  if (filters.search) params.set("search", filters.search);
  return apiRequest(`/api/v1/admin/review-assignments?${params}`, { signal });
}

export function createReviewAssignments(
  projectIds: string[],
  reviewerUserIds: string[],
): Promise<ReviewAssignmentCreateResponse> {
  return apiRequest("/api/v1/admin/review-assignments", {
    method: "POST",
    body: JSON.stringify({ project_ids: projectIds, reviewer_user_ids: reviewerUserIds }),
  });
}

export function revokeReviewAssignment(id: string): Promise<ReviewAssignment> {
  return apiRequest(`/api/v1/admin/review-assignments/${id}`, { method: "DELETE" });
}

export function reassignReviewAssignment(
  id: string,
  reviewerUserId: string,
): Promise<ReviewAssignment> {
  return apiRequest(`/api/v1/admin/review-assignments/${id}/reassign`, {
    method: "POST",
    body: JSON.stringify({ reviewer_user_id: reviewerUserId }),
  });
}

export function fetchReviewTasks(
  filters: {
    page: number;
    pageSize: number;
    search?: string;
    workTypeCode?: string;
    assignedFrom?: string;
    assignedTo?: string;
  },
  signal?: AbortSignal,
): Promise<ReviewTaskListResponse> {
  const params = new URLSearchParams({
    page: String(filters.page),
    page_size: String(filters.pageSize),
  });
  if (filters.search) params.set("search", filters.search);
  if (filters.workTypeCode) params.set("work_type_code", filters.workTypeCode);
  if (filters.assignedFrom) params.set("assigned_from", filters.assignedFrom);
  if (filters.assignedTo) params.set("assigned_to", filters.assignedTo);
  return apiRequest(`/api/v1/review-tasks?${params}`, { signal });
}

export function fetchAdminReviewTasks(
  filters: {
    page: number;
    pageSize: number;
    search?: string;
    workTypeCode?: string;
    reviewerUserId?: string;
    assignedFrom?: string;
    assignedTo?: string;
  },
  signal?: AbortSignal,
): Promise<ReviewTaskListResponse> {
  const params = new URLSearchParams({
    page: String(filters.page),
    page_size: String(filters.pageSize),
  });
  if (filters.search) params.set("search", filters.search);
  if (filters.workTypeCode) params.set("work_type_code", filters.workTypeCode);
  if (filters.reviewerUserId) params.set("reviewer_user_id", filters.reviewerUserId);
  if (filters.assignedFrom) params.set("assigned_from", filters.assignedFrom);
  if (filters.assignedTo) params.set("assigned_to", filters.assignedTo);
  return apiRequest(`/api/v1/admin/review-tasks?${params}`, { signal });
}

export function fetchReviewTaskDetail(id: string): Promise<ReviewTaskDetail> {
  return apiRequest(`/api/v1/review-tasks/${id}`);
}

export function fetchReviewTaskCloudDriveInfo(id: string): Promise<ProjectCloudDriveInfoResponse> {
  return apiRequest(`/api/v1/review-tasks/${id}/cloud-drive-info`);
}

export function recordReviewTaskCloudDriveInfoCopy(id: string): Promise<void> {
  return apiRequest(`/api/v1/review-tasks/${id}/cloud-drive-info/copy-audit`, {
    method: "POST",
  });
}
